import { T } from '@tolgee/react';
import Link from 'next/link';
import { ReactNode } from 'react';

import DownloadSvg from '../../../assets/icons/DownloadSvg';
import theme from '../../../assets/theme';
import { useMetrics } from '../../../hooks/useMetrics';
import { Button } from '../../_atoms/Button';

export type FaqItem = {
  key: string;
  label: ReactNode;
  content: ReactNode;
};

const W9_DOWNLOAD_URL =
  'https://drive.google.com/uc?export=download&id=1FkY9xPB7DRLAxm3ujfUb9SI1Qg-OGN0N';
const PAYMENT_DOC_URL =
  'https://curipod.notion.site/Curipod-Payment-Document-c18013fc71594bf2ab8b49f276c9d54e?pvs=4';
const DPA_FORM_URL = 'https://share.hsforms.com/1Nwgx-5qBT_euEVjxG-eztw4evga';

/**
 * The single FAQ list, shared by /pricing and /quotes.
 *
 * Answer copy comes verbatim from the marketing design; where the previous FAQ
 * carried a working affordance the design copy has no room for (the W-9
 * download, the payment document, the DPA intake form, the discount breakdown),
 * that affordance is appended below the design copy rather than replacing it.
 *
 * It is a hook rather than a constant because several answers log click events.
 */
export const useFaqItems = (): FaqItem[] => {
  const metrics = useMetrics(true);

  return [
    {
      key: 'free-plan-limits',
      label: (
        <T
          keyName="Pricing.Faq.FreePlanLimits.Q"
          defaultValue="How much writing practice does the free plan include?"
        />
      ),
      content: (
        <p>
          <T
            keyName="Pricing.Faq.FreePlanLimits.A"
            defaultValue="The free plan includes a set number of live sessions each week — enough for regular writing practice with your classes. The allowance renews automatically every week, and the number of students who can write is always unlimited."
          />
          <br />
          <br />
          <a
            href="https://curipod.com/school-and-free-plan"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              metrics.logEvent(
                'PricingPage.faq.teachingSessions.learnMoreLimits.clicked',
              );
            }}
          >
            <T
              keyName="Learn more about the free plan limits"
              defaultValue="Learn more about the free plan limits"
            />
          </a>
        </p>
      ),
    },
    {
      key: 'school-site-license',
      label: (
        <T
          keyName="Pricing.Faq.SchoolSiteLicense.Q"
          defaultValue="How do I buy a Curipod school site license?"
        />
      ),
      content: (
        <p>
          <Link href="/quotes?step=form">
            <a
              onClick={() => {
                metrics.logEvent('PricingPage.faq.schoolLicenceForm.clicked');
              }}
            >
              <T
                keyName="Pricing.Faq.SchoolSiteLicense.A.Link"
                defaultValue="Request a quote"
              />
            </a>
          </Link>{' '}
          <T
            keyName="Pricing.Faq.SchoolSiteLicense.A.Rest"
            defaultValue="and we'll send a formal quote for your school or district, sized to your student count. From there you can purchase by card or by purchase order."
          />
        </p>
      ),
    },
    {
      key: 'how-can-i-pay',
      label: <T keyName="Pricing.Faq.HowToPay.Q" defaultValue="How can I pay?" />,
      content: (
        <p>
          <T
            keyName="Pricing.Faq.HowToPay.A"
            defaultValue="Credit card, invoice, or purchase order."
          />
          <br />
          <br />
          <a href={PAYMENT_DOC_URL} target="_blank" rel="noopener noreferrer">
            <T
              keyName="Pricing.Faq.HowToPay.A.DocLink"
              defaultValue="Read the full payment document"
            />
          </a>
        </p>
      ),
    },
    {
      key: 'cooperatives',
      label: (
        <T
          keyName="Pricing.Faq.Cooperatives.Q"
          defaultValue="Which cooperatives is Curipod awarded on?"
        />
      ),
      content: (
        <p>
          <T
            keyName="Pricing.Faq.Cooperatives.A"
            defaultValue="We work with common school purchasing platforms, including TIPS, Allied States (ESC 19), and others. Reach out to"
          />{' '}
          <a
            href="mailto:lexi@curipod.com"
            onClick={() => {
              metrics.logEvent('PricingPage.faq.cooperatives.emailLexi.clicked');
            }}
          >
            lexi@curipod.com
          </a>{' '}
          <T keyName="Pricing.Faq.Cooperatives.A.End" defaultValue="with questions." />
        </p>
      ),
    },
    {
      key: 'w9',
      label: <T keyName="Pricing.Faq.W9.Q" defaultValue="Do you have a W-9?" />,
      content: (
        <p>
          <T
            keyName="Pricing.Faq.W9.A"
            defaultValue="Yes — ask us and we'll send the current W-9 along with any other vendor paperwork your district needs."
          />
          <br />
          <a
            style={{ textDecoration: 'none' }}
            href={W9_DOWNLOAD_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              metrics.logEvent('PricingPage.faq.W9Download.clicked');
            }}
          >
            <Button
              styles={{ border: 'none', boxShadow: 'none', paddingLeft: 0 }}
              type="ghost"
              icon={<DownloadSvg />}
            >
              <T keyName="Form W-9 Curipod.pdf" defaultValue="Form W-9 Curipod.pdf" />
            </Button>
          </a>
        </p>
      ),
    },
    {
      key: 'discounts',
      label: (
        <T
          keyName="Pricing.Faq.Discounts.Q"
          defaultValue="Do you offer any discounts or other incentives?"
        />
      ),
      content: (
        <div>
          <p>
            <T
              keyName="Pricing.Faq.Discounts.A"
              defaultValue="Multi-school, district-wide, and multi-year agreements are discounted. Tell us the scope in your quote request and we'll include the best pricing we can."
            />
          </p>
          <ul>
            <li>
              <strong>
                <T
                  keyName="Small School Discount:"
                  defaultValue="Small School Discount:"
                />
              </strong>{' '}
              <T
                keyName="If your school is less than 200 students, we have a small school price for you. Email lexi@curipod.com for a custom quote."
                defaultValue="If your school is less than 200 students, we have a small school price for you. Email lexi@curipod.com for a custom quote."
              />
            </li>
            <li>
              <strong>
                <T
                  keyName="Financial Aid Discounts:"
                  defaultValue="Financial Aid Discounts:"
                />
              </strong>{' '}
              <T
                keyName="We don't want money to be a barrier to accessing Curipod! Please contact us if your school cannot afford Curipod. We are happy to connect with your administration so that we can find a way for you to access Curipod premium features. Email lexi@curipod.com for support."
                defaultValue="We don't want money to be a barrier to accessing Curipod! Please contact us if your school cannot afford Curipod. We are happy to connect with your administration so that we can find a way for you to access Curipod premium features. Email lexi@curipod.com for support."
              />
            </li>
            <li>
              <strong>
                <T
                  keyName="Grants & Funding Options:"
                  defaultValue="Grants & Funding Options:"
                />
              </strong>{' '}
              <T
                keyName="Our partnerships team are experts that understanding budgets and helping you fit Curipod into funding sources. Email lexi@curipod.com for support."
                defaultValue="Our partnerships team are experts that understanding budgets and helping you fit Curipod into funding sources. Email lexi@curipod.com for support."
              />
            </li>
          </ul>
        </div>
      ),
    },
    {
      key: 'data-processing',
      label: (
        <T
          keyName="Pricing.Faq.DataProcessing.Q"
          defaultValue="How do you process data?"
        />
      ),
      content: (
        <p>
          <T
            keyName="Pricing.Faq.DataProcessing.A"
            defaultValue="Curipod is FERPA-, COPPA-, SOPPA- and GDPR-compliant. Student and teacher data is never used to train AI models, and students join without creating accounts. Read our"
          />{' '}
          <Link href="/privacy-policy">
            <a
              onClick={() => {
                metrics.logEvent(
                  'PricingPage.faq.stateAndDistrictPrivacyAgreements.clicked',
                );
              }}
            >
              <T
                keyName="Pricing.Faq.DataProcessing.A.Link"
                defaultValue="privacy policy"
              />
            </a>
          </Link>
          .
        </p>
      ),
    },
    {
      key: 'sign-dpa',
      label: (
        <T
          keyName="Pricing.Faq.SignDpa.Q"
          defaultValue="Can you sign our data privacy agreement?"
        />
      ),
      content: (
        <p>
          <T
            keyName="Pricing.Faq.SignDpa.A"
            defaultValue="Yes. Signing custom data privacy agreements are included on the School & District plan. If you are on, or planning to sign onto a school or district plan, send your agreement over to"
          />{' '}
          <a
            href="mailto:rebecca@curipod.com"
            onClick={() => {
              metrics.logEvent('PricingPage.faq.emailDataPrivacyQuestions.clicked');
            }}
          >
            rebecca@curipod.com
          </a>
          <T keyName="Pricing.Faq.SignDpa.A.End" defaultValue=", and we'll review it." />
          <br />
          <br />
          <a
            href={DPA_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              metrics.logEvent('PricingPage.faq.signDataPrivacy.clicked');
            }}
          >
            <T
              keyName="Pricing.Faq.SignDpa.A.FormLink"
              defaultValue="Submit your agreement through our form"
            />
          </a>
        </p>
      ),
    },
    {
      key: 'other-questions',
      label: <T keyName="Other questions?" defaultValue="Other questions?" />,
      content: (
        <p>
          <T keyName="Pricing.Faq.OtherQuestions.A" defaultValue="Reach out to" />{' '}
          <a
            style={{ color: theme.colors.blue }}
            href="mailto:lexi@curipod.com"
            onClick={() => {
              metrics.logEvent('PricingPage.faq.emailOtherQuestions.clicked');
            }}
          >
            lexi@curipod.com
          </a>{' '}
          <T
            keyName="Pricing.Faq.OtherQuestions.A.End"
            defaultValue="and a she will help you out!"
          />
        </p>
      ),
    },
  ];
};
